"use client"

import { useEffect, useState } from "react"
import { Link } from "@tanstack/react-router"
import { MoreHorizontal, Search } from "lucide-react"

import { CommandPalette } from "@/components/command-palette"
import { ThemeToggle } from "@/components/theme-toggle"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/hooks/useAuth"
import { OVERFLOW_NAV, PRIMARY_NAV } from "./nav-routes"
import { cn } from "@/lib/utils"
import { QuickEmailDialog } from "./quick-email-dialog"

/**
 * Structure follows the Graphite canvas (`Reimagining Recruiter OS.dc.html`,
 * artboard 12a): a 56px bar holding logo, wordmark, five text routes, a 280px
 * command field, and the avatar — and nothing else. The bar is deliberately
 * spare; that restraint is most of why it reads as a modern product rather than
 * a 2015 ATS.
 *
 * Routes are text only with a 2px underline for the active item. No icons: the
 * canvas has none, and they cost horizontal space the bar cannot spare inside a
 * resizable panel.
 *
 * Secondary actions the old topbar carried (New Search, quick email, theme) are
 * not in the canvas bar. They moved to the overflow and avatar menus rather than
 * being deleted.
 */

/**
 * `align-self: stretch` + a negative bottom margin so the underline sits on the
 * header's own hairline, as in the canvas.
 */
const navLinkClass =
  "-mb-px inline-flex shrink-0 items-center whitespace-nowrap border-b-2 border-transparent px-0.5 text-[13px] font-normal text-muted-foreground transition-colors hover:text-foreground"

const navLinkActiveClass = "border-primary font-medium text-foreground"

export function TopNav() {
  const { user, logout } = useAuth()
  const [commandOpen, setCommandOpen] = useState(false)
  const userInitial = (
    user?.name?.trim()?.[0] ??
    user?.email?.trim()?.[0] ??
    "U"
  ).toUpperCase()

  // ⌘K belongs to the ATS command palette. The conversation sidebar used to
  // register a second listener on the same chord, so both dialogs opened at once;
  // chat search is ⌘⇧K now.
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
        e.preventDefault()
        setCommandOpen((open) => !open)
      }
    }
    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  return (
    // `@container`, not viewport breakpoints: this bar lives inside a resizable
    // panel, so the only width that matters is its own. At a 65/35 split on a
    // 1280px screen it has ~830px, where viewport `lg:` is still true and the
    // contents would overlap.
    // `hidden md:flex`: artboard 12b has no app bar on a phone. Navigation is the
    // bottom nav and each page supplies its own pinned header, so a second bar
    // would just eat vertical space.
    <header className="@container sticky top-0 z-50 hidden h-14 w-full shrink-0 items-stretch gap-2.5 border-b bg-chrome px-[18px] md:flex">
      <Link
        to="/dashboard"
        className="flex shrink-0 items-center gap-2.5 pr-3.5"
        aria-label="Recruiter OS"
      >
        <span className="flex h-[26px] w-[26px] items-center justify-center rounded-md bg-primary text-[13px] font-bold text-primary-foreground">
          R
        </span>
        <span className="hidden text-[13px] font-semibold @3xl:inline">
          Recruiter OS
        </span>
      </Link>

      {/* min-w-0 + overflow-hidden so the nav truncates rather than growing into
          the right-hand group and intercepting its clicks. */}
      <nav
        aria-label="Main"
        className="flex min-w-0 flex-1 items-stretch gap-5 overflow-hidden"
      >
        {PRIMARY_NAV.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            className={navLinkClass}
            activeProps={{ className: cn(navLinkClass, navLinkActiveClass) }}
          >
            {item.name}
          </Link>
        ))}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(navLinkClass, "px-1")}
              aria-label="More"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            {OVERFLOW_NAV.map((item) => (
              <DropdownMenuItem key={item.href} asChild>
                <Link to={item.href}>{item.name}</Link>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/searches/new">New search</Link>
            </DropdownMenuItem>
            <div className="px-1 py-0.5">
              <QuickEmailDialog />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>

      <div className="flex shrink-0 items-center gap-3">
        <button
          type="button"
          onClick={() => setCommandOpen(true)}
          className="hidden h-[34px] w-[280px] items-center gap-2.5 rounded-md border bg-background px-3 text-left text-xs text-muted-foreground transition-colors hover:border-foreground/20 @5xl:flex"
        >
          <Search className="h-[15px] w-[15px] shrink-0" />
          <span className="flex-1 truncate">Search or run a command</span>
          <span className="shrink-0 font-mono text-[10.5px]">⌘K</span>
        </button>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 @5xl:hidden"
          onClick={() => setCommandOpen(true)}
          aria-label="Search or run a command"
        >
          <Search className="h-4 w-4" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Account"
            >
              <Avatar className="h-[30px] w-[30px]">
                <AvatarImage src={user?.picture ?? undefined} alt="" />
                <AvatarFallback className="bg-primary/15 text-[11px] font-medium text-brand-ink">
                  {userInitial}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.name ?? ""}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email ?? ""}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings">Settings</Link>
            </DropdownMenuItem>
            <div className="flex items-center justify-between px-2 py-1.5 text-sm">
              <span>Theme</span>
              <ThemeToggle />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>Log out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
    </header>
  )
}
